import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    Self,
} from '@angular/core';
import { AbstractControl, NgControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BazisFileService } from '@bazis/form/services/file.service';
import { FILE_EXTENSIONS, FileDisplayType, FileType } from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { BazisToastService } from '@bazis/shared/services/toast.service';
import { BazisConfigurationService, MAX_FILE_SIZE } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-file',
    templateUrl: './input-file.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputFileComponent extends BazisControlValueAccessor {
    @HostBinding('class.bazis-input-file') get getHostClass() {
        return true;
    }

    @Input() displayType: FileDisplayType = 'default';

    // макс. кол-во файлов
    @Input() max: number = 1;

    // Сколько файлов надо проинициализировать пустыми, например
    @Input() initNumber: number = 1;

    // текст кнопки
    @Input() buttonKey: string = 'form.title.inputFile.addFiles';

    // параметры для кнопки
    @Input() buttonParams: any = null;

    @Input() fileType: FileType = 'any';

    @Input() imgAlt: string = '';

    @Input() maxFileSize = MAX_FILE_SIZE;

    @Input() directExtentions: string[] = null;

    fileIds: string[] = [];

    extentions = FILE_EXTENSIONS[this.fileType];

    isDisabled: boolean = false;

    private uploadingDict: { [key: string]: any } = {};

    private _needUpdateUploadingDict$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        true,
    );

    uploadingDict$ = this._needUpdateUploadingDict$.pipe(map(() => this.uploadingDict));

    _isInited = false;

    private _lastValuesSentToControl = null;

    displayAllowedExtentions: string[];

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        public configurationService: BazisConfigurationService,
        private fileService: BazisFileService,
        private toastService: BazisToastService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    extendOnInit() {
        this.ngControl.control.addValidators([this.loadingValidator(this.uploadingDict)]);
        this.displayAllowedExtentions = this.directExtentions || FILE_EXTENSIONS[this.fileType];
        this._isInited = true;
    }

    loadingValidator(uploadingDict): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            return Object.keys(uploadingDict).length > 0 ? { loadingInProcess: true } : null;
        };
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    fileChanged(id: string, e) {
        const element = e.currentTarget as HTMLInputElement;
        let fileList: FileList | null = element.files;

        if (fileList.length === 1) {
            const file = fileList[0];

            this.preUploadFile(file, element, id);
            this.uploadFile(file, id);
        }
        this.ngControl.control.updateValueAndValidity();
    }

    preUploadFile(file: File, element: HTMLInputElement, id: string) {
        // this.ngControl.control.setErrors(null);

        const result = this.fileService.preUploadFile(
            file,
            element,
            this.directExtentions || FILE_EXTENSIONS[this.fileType],
            this.maxFileSize,
        );

        if (result.error) {
            // this.ngControl.control.setErrors({ [result.error]: true });
            this.removeFile(this.fileIds.findIndex((v) => v === id));
            return;
        }

        this.uploadingDict[id] = {
            id,
            file,
            name: file.name,
            size: file.size,
            extension: result.extension,
            status: 'progress',
            progress: 0,
            isImage: FILE_EXTENSIONS.image.indexOf(result.extension) > -1,
        };
    }

    checkRealMimeType(file: File, header: string) {
        return this.fileService.checkRealMimeType(
            file,
            header,
            this.directExtentions || FILE_EXTENSIONS[this.fileType],
        );
    }

    uploadFile(file: File, id: string) {
        this.fileService
            .readFile$(file, this.directExtentions || FILE_EXTENSIONS[this.fileType])
            .pipe(
                mergeMap((v) => this.fileService.uploadFile$(file)),
                tap((r) => {
                    if (!this.uploadingDict[id]) return;
                    this.uploadingDict[id].status = r.state.status;
                    this.uploadingDict[id].progress = r.state.progress;
                    if (this.uploadingDict[id].status === 'success') {
                        const index = this.fileIds.findIndex((v) => v === id);
                        this.fileIds[index] = r.state.id;
                        delete this.uploadingDict[id];
                        this.updateFormControl();
                    }
                    this._needUpdateUploadingDict$.next(true);
                }),
                catchError((e) => {
                    if (e?.error === 'mimeTypeError') {
                        // this.ngControl.control.setErrors({ fileErrorMime: true });
                        this.removeFile(this.fileIds.findIndex((v) => v === id));
                    }
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    removeFile(index) {
        const fileId = this.fileIds[index];
        this.fileIds.splice(index, 1);
        if (this.uploadingDict[fileId]) {
            delete this.uploadingDict[fileId];
            this._needUpdateUploadingDict$.next(true);
        } else {
            this.updateFormControl();
        }
        this.markAsTouched();
    }

    addFile(omitMarkAsTouched = false) {
        const id = `${uuidv4()}`;
        if (this.fileIds.length < this.max) {
            this.uploadingDict[id] = { status: 'empty' };
            this.fileIds.push(id);
            this._needUpdateUploadingDict$.next(true);
        }
        if (!omitMarkAsTouched) {
            this.markAsTouched();
        }
        return id;
    }

    get lastFileId(): string {
        return this.fileIds[this.fileIds.length - 1];
    }

    addFiles(e) {
        const element = e.currentTarget as HTMLInputElement;
        let fileList: FileList | null = element.files;

        for (let index = 0; index < fileList.length; index++) {
            const file = fileList[index];
            const id = this.addFile();
            this.preUploadFile(file, element, this.lastFileId);
            if (!this.uploadingDict[id]) return;

            this.uploadFile(file, this.lastFileId);
        }
        this.ngControl.control.updateValueAndValidity();
        element.value = '';
    }

    updateFormControl() {
        const values = this.fileIds.filter((v) => v && !this.uploadingDict[v]);
        const value = this.max > 1 ? values : values.length ? values[0] : null;
        this._lastValuesSentToControl = value;
        this.onChange(value);
    }

    public writeValue(value): void {
        if (!value || !this._isInited) return;

        if (this.max === 1) {
            if (value && this._lastValuesSentToControl !== value) {
                if (Array.isArray(value)) {
                    this.fileIds = [...value];
                } else {
                    this.fileIds = [];
                    this.fileIds.push(value);
                }
            }
        } else {
            if (
                value.length &&
                (this._lastValuesSentToControl || []).join(',') !== value.join(',')
            ) {
                this.fileIds = value.concat(this.fileIds.filter((v) => this.uploadingDict[v]));
            }
        }
    }
}
